import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PiggyBank, KeyRound, EyeOff, Sparkles, Mic, Search, Handshake, ArrowRight } from "lucide-react";

const GAINS = [
  { icon: KeyRound, t: "L'accès avant tout le monde", d: "Des biens confidentiels, proposés au réseau et à nos acquéreurs avant leur diffusion publique — souvent jamais visibles ailleurs." },
  { icon: PiggyBank, t: "Un meilleur prix, des deux côtés", d: "Sans mise en concurrence agressive ni surenchère : une négociation sereine, des honoraires maîtrisés et de vraies économies." },
  { icon: EyeOff, t: "La discrétion absolue", d: "Vendre ou acheter sans exposer votre projet : pas de panneau, pas de vitrine, pas de portails. Tout circule dans la confiance." },
  { icon: Sparkles, t: "La puissance de l'IA", d: "Description à la voix, mise en relation intelligente et matching automatique : la technologie travaille pour votre projet." },
];

const STEPS = [
  { icon: Mic, t: "Vous décrivez votre projet", d: "À la voix ou par écrit, en quelques secondes." },
  { icon: Search, t: "L'IA + le réseau cherchent", d: "Vos critères sont confrontés aux mandats off market du réseau." },
  { icon: KeyRound, t: "Vous accédez en priorité", d: "Les biens correspondants vous remontent avant le marché." },
  { icon: Handshake, t: "On vous accompagne", d: "Un conseiller dédié, du premier contact jusqu'à la signature." },
];

export function WhyOffMarket({ accent = "#C19A6B", joinTo = "/rejoindre", joinLabel = "Rejoindre OFF MARKETTE", name = "", id = "why-off-market", audience = "join" }) {
  const { t } = useTranslation();
  const isBuyer = audience === "buyer";
  const ctaTo = isBuyer ? "/espace-acquereurs" : joinTo;
  const ctaLabel = isBuyer ? t("why.buy_cta") : joinLabel;
  const ac = { color: accent };
  const acBg = { backgroundColor: accent };
  const acBorder = { borderColor: `${accent}55` };
  return (
    <>
    <section id={id} data-testid="why-off-market" className="relative overflow-hidden bg-[#080809] text-white py-24 sm:py-32">
      <div aria-hidden="true" className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${accent}1f` }} />
      <div aria-hidden="true" className="absolute -bottom-32 -right-24 w-[32rem] h-[32rem] rounded-full blur-3xl pointer-events-none" style={{ backgroundColor: `${accent}14` }} />
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* En-tête asymétrique */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-7">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase" style={ac}>Le modèle OFF MARKET</span>
            <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.08]">
              {isBuyer
                ? <>{t("why.buy_title")} <br className="hidden sm:block" /><span className="font-semibold" style={ac}>{t("why.buy_title_hl")}</span></>
                : <>Pourquoi rejoindre <br className="hidden sm:block" /><span className="font-semibold" style={ac}>OFF MARKETTE</span> ?</>}
            </h2>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} className="lg:col-span-5 text-base sm:text-lg font-light text-white leading-relaxed">
            L'<strong className="text-white font-normal">off market</strong>, c'est l'immobilier discret : des biens qui s'échangent {name ? <>avec {name} et </> : null}au sein d'un réseau de confiance, <strong className="text-white font-normal">avant</strong> d'arriver sur les portails. Plus d'exclusivité, de sérénité et d'économies — pour les vendeurs comme pour les acquéreurs.
          </motion.p>
        </div>

        {/* Les gains — cadres sur fond gris clair */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {GAINS.map((g, i) => (
            <motion.div key={g.t} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative rounded-[5px] p-6 hover:shadow-lg transition-shadow duration-500 ${i % 2 === 1 ? "bg-white" : "bg-[#D4D6DB]"}`}>
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-[5px] border" style={{ ...acBorder, color: accent, backgroundColor: `${accent}14` }}>
                <g.icon className="w-5 h-5" />
              </span>
              <h3 className="mt-5 text-base font-medium text-[#050505] leading-snug">{g.t}</h3>
              <p className="mt-2 text-sm font-light text-zinc-600 leading-relaxed">{g.d}</p>
              <span aria-hidden="true" className="absolute left-6 bottom-0 h-px w-0 group-hover:w-12 transition-all duration-500" style={acBg} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Comment ça marche — section pleine largeur, fond blanc */}
    <section className="bg-white text-[#050505] py-20 sm:py-28" data-testid="why-how">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex items-center gap-4 mb-10">
          <span className="text-xs font-semibold tracking-[0.3em] uppercase" style={ac}>Comment ça marche</span>
          <span className="flex-1 h-px bg-black/10" />
        </motion.div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.5, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }} className="relative">
              <div className="flex items-center gap-3">
                <span className="text-2xl font-light tabular-nums" style={ac}>0{i + 1}</span>
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-zinc-300 text-zinc-600"><s.icon className="w-4 h-4" /></span>
              </div>
              <h4 className="mt-4 text-base font-medium text-[#050505]">{s.t}</h4>
              <p className="mt-1.5 text-sm font-light text-zinc-600 leading-relaxed">{s.d}</p>
              {i < STEPS.length - 1 && <span aria-hidden="true" className="hidden lg:block absolute top-4 -right-3 text-black/15"><ArrowRight className="w-5 h-5" /></span>}
            </motion.div>
          ))}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="mt-14 flex flex-wrap items-center gap-5">
          <Link to={ctaTo} data-testid="why-join-cta" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-[5px] font-medium uppercase tracking-wider text-sm text-white transition-transform duration-300 hover:-translate-y-0.5" style={acBg}>
            {ctaLabel} <ArrowRight className="w-4 h-4" />
          </Link>
          {!isBuyer && <span className="text-sm font-light text-zinc-500">Sans engagement — en toute confidentialité.</span>}
        </motion.div>
      </div>
    </section>
    </>
  );
}
